import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiSearch, FiDatabase, FiBarChart2, FiFilter, FiExternalLink,
  FiChevronDown, FiChevronUp, FiBook, FiGlobe, FiCalendar,
  FiFileText, FiX, FiInfo, FiCheckCircle, FiAlertTriangle,
  FiPieChart,
} from 'react-icons/fi';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie,
} from 'recharts';
import { api } from '../api';
import { useAgentChat } from '../context/AgentChatContext';
import { getStatusMeta } from '../utils/validationStatus';
import './KnowledgeBase.css';

const CHART_COLORS = ['#1E3A8A', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];
const TIER_LABELS = { 1: 'Tier 1 - IB / Big4', 2: 'Tier 2 - Specialized', 3: 'Tier 3 - Independent' };
const TIER_COLORS = { 1: '#1E3A8A', 2: '#3b82f6', 3: '#94a3b8' };

function KnowledgeBase() {
  // Intentionally read-only: drilldowns from other pages (dashboard, company detail)
  // land here with pre-filled ?tab/?q/?source/etc. Local refinements stay client-side
  // to avoid URL churn on every keystroke/filter toggle. Restore setSearchParams if
  // we need shareable deep-links from this page itself.
  const [searchParams] = useSearchParams();
  const { updatePageContext, clearPageContext } = useAgentChat();

  // ── Data state ──
  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedChunks, setExpandedChunks] = useState({});
  const [metadataPopup, setMetadataPopup] = useState(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewNoteInput, setReviewNoteInput] = useState('');
  const [showReviewNote, setShowReviewNote] = useState(false);
  const [intelTooltip, setIntelTooltip] = useState(null); // {id, text, x, y}
  const [showMethodology, setShowMethodology] = useState(false);
  const [flagForm, setFlagForm] = useState(null); // {id, source, file_id, chunk_id}
  const [flagText, setFlagText] = useState('');
  const [flagField, setFlagField] = useState('source');
  const [flagSaving, setFlagSaving] = useState(false);
  const [flagSuccess, setFlagSuccess] = useState(null); // id of last flagged

  // ── Source Intelligence state ──
  const [topicTrends, setTopicTrends] = useState(null);
  const [entityVelocity, setEntityVelocity] = useState(null);
  const [geoShifts, setGeoShifts] = useState(null);
  const [tierComposition, setTierComposition] = useState(null);
  const [convergence, setConvergence] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);

  // ── Cross-Validation state ──
  const [valHeadlines, setValHeadlines] = useState(null);
  const [valMetrics, setValMetrics] = useState(null);
  const [valAgreement, setValAgreement] = useState(null);
  const [valLoading, setValLoading] = useState(false);
  const [valExpandedRow, setValExpandedRow] = useState(null);
  const [valClaims, setValClaims] = useState(null);
  const [valFilters, setValFilters] = useState({ category: '', year: '', min_quality: '', status: '' });
  const [valClusters, setValClusters] = useState(null);
  const [valClusterExpanded, setValClusterExpanded] = useState(null);
  const [matrixHover, setMatrixHover] = useState(null); // {cat, src, claims, x, y}

  // ── Dashboard enrichment state ──
  const [sourcesData, setSourcesData] = useState(null);
  const [dashYearPreview, setDashYearPreview] = useState(null); // {year, docs}

  // ── Ingestion Audit state ──
  // Paste a GDrive folder URL → list its files → check which are ingested in
  // the knowledge index. The entry point that surfaces the existing
  // cross-validation surface for "compare reliable sources".
  const [auditFolderUrl, setAuditFolderUrl] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  // ── Browse sort state ──
  const [sortBy, setSortBy] = useState('document_year');
  const [sortDir, setSortDir] = useState('desc');

  // ── UI state ──
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'browse');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchMode, setSearchMode] = useState('hybrid'); // hybrid | semantic | lexical
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  // ── Filters ──
  const [filters, setFilters] = useState({
    source: searchParams.get('source') || '',
    source_tier: searchParams.get('tier') || '',
    report_type: searchParams.get('type') || '',
    document_year: searchParams.get('year') || '',
    geographic_focus: searchParams.get('geo') || '',
    topics: searchParams.get('topics') || '',
    vertical: searchParams.get('vertical') || '',
    platform: searchParams.get('platform') || '',
    status: 'Embedded',
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  // ── Close metadata popup on outside click ──
  useEffect(() => {
    const handleClickOutside = () => setMetadataPopup(null);
    if (metadataPopup) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [metadataPopup]);

  // ── Escape-to-close for the methodology modal ──
  useEffect(() => {
    if (!showMethodology) return;
    const handleEscape = (e) => { if (e.key === 'Escape') setShowMethodology(false); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showMethodology]);

  // ── Auto-clear flag-success indicator after 3s (cleaned up on unmount / re-flag) ──
  useEffect(() => {
    if (!flagSuccess) return;
    const t = setTimeout(() => setFlagSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [flagSuccess]);

  // ── Fetch tier composition for dashboard (lightweight) ──
  useEffect(() => {
    if (tierComposition) return;
    if (activeTab === 'dashboard' || activeTab === 'intelligence') {
      api.getTierComposition().then(tc => setTierComposition(tc)).catch(() => {});
    }
  }, [activeTab, tierComposition]);

  // ── Fetch intelligence data when tab selected ──
  useEffect(() => {
    if (activeTab !== 'intelligence' || topicTrends) return;
    const fetchIntelligence = async () => {
      setIntelLoading(true);
      try {
        const [tt, ev, gs, cv] = await Promise.all([
          api.getTopicTrends(),
          api.getEntityVelocity({ min_sources: 3, top_k: 20 }),
          api.getGeoShifts(),
          api.getConvergence(),
        ]);
        setTopicTrends(tt);
        setEntityVelocity(ev);
        setGeoShifts(gs);
        setConvergence(cv);
      } catch (e) {
        console.error('Intelligence fetch failed:', e);
      } finally {
        setIntelLoading(false);
      }
    };
    fetchIntelligence();
  }, [activeTab, topicTrends]);

  // ── Fetch validation data when tab selected ──
  useEffect(() => {
    if (activeTab !== 'validation' || valHeadlines) return;
    const fetchValidation = async () => {
      setValLoading(true);
      try {
        const [headlines, metrics, agreement, claimsData, clustersData] = await Promise.all([
          api.getValidationHeadlineMetrics(),
          api.getValidationMetrics(),
          api.getValidationAgreementMatrix(),
          api.getValidationClaims({ limit: 50 }),
          api.getValidationClusters({ min_sources: 2, limit: 20 }),
        ]);
        setValHeadlines(headlines);
        setValMetrics(metrics);
        setValAgreement(agreement);
        setValClaims(claimsData);
        setValClusters(clustersData);
      } catch (e) {
        console.error('Validation fetch failed:', e);
      } finally {
        setValLoading(false);
      }
    };
    fetchValidation();
  }, [activeTab, valHeadlines]);

  // ── Fetch filtered validation metrics ──
  const fetchFilteredMetrics = useCallback(async (newFilters) => {
    try {
      const params = {};
      if (newFilters.category) params.category = newFilters.category;
      if (newFilters.year) params.year = newFilters.year;
      if (newFilters.min_quality) params.min_quality = parseInt(newFilters.min_quality);
      if (newFilters.status) params.status = newFilters.status;
      const result = await api.getValidationMetrics(params);
      setValMetrics(result);
    } catch (e) {
      console.error('Filtered metrics fetch failed:', e);
    }
  }, []);

  // ── Fetch claims for expanded metric row ──
  const fetchClaimsForMetric = useCallback(async (category, year) => {
    try {
      const result = await api.getValidationClaims({ category, year, limit: 30 });
      setValClaims(result);
    } catch (e) {
      console.error('Claims fetch failed:', e);
    }
  }, []);

  // ── Fetch filter options on mount ──
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [filtersData, timelineData, statsData, srcData] = await Promise.all([
          api.getFilters(),
          api.getTimeline(),
          api.getStats(),
          api.getSources(),
        ]);
        setFilterOptions(filtersData);
        setTimeline(timelineData?.timeline || []);
        setStats(statsData);
        setSourcesData(srcData);
      } catch (e) {
        console.error('Failed to load knowledge base metadata:', e);
      }
    };
    fetchFilters();
  }, []);

  // ── Fetch reports when filters/sort change ──
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const data = await api.listReports(params);
      setReports(data?.results || []);
      setTotalReports(data?.total || 0);
    } catch (e) {
      console.error('Failed to fetch reports:', e);
    } finally {
      setLoading(false);
    }
  }, [filters, page, sortBy, sortDir]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // ── Agent chat context ──
  useEffect(() => {
    updatePageContext('knowledge-base', null, {
      resultCount: totalReports,
      activeFilters: Object.entries(filters).filter(([, v]) => v && v !== 'Embedded').map(([k, v]) => `${k}=${v}`),
      searchQuery: searchQuery || null,
      tab: activeTab,
    });
    return () => clearPageContext();
  }, [totalReports, filters, searchQuery, activeTab, updatePageContext, clearPageContext]);

  // ── Search handler ──
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setActiveTab('search');
    try {
      const params = { query: searchQuery, top_k: 20, mode: searchMode };
      if (filters.source) params.source = filters.source;
      if (filters.source_tier) params.source_tier = parseInt(filters.source_tier);
      if (filters.report_type) params.report_type = filters.report_type;
      if (filters.document_year) params.document_year = parseInt(filters.document_year);
      if (filters.geographic_focus) params.geographic_focus = [filters.geographic_focus];
      if (filters.topics) params.topics = [filters.topics];
      const data = await api.searchKnowledge(params);
      setSearchResults(data);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearchLoading(false);
    }
  };

  // ── Ingestion audit handler ──
  // Folder URL → list files via GDrive → check coverage in knowledge index.
  // Result joins both sides so missing files carry names (not just IDs).
  const handleAuditCheck = async () => {
    const folderInput = auditFolderUrl.trim();
    if (!folderInput) {
      setAuditError('Paste a GDrive folder URL or ID first');
      return;
    }
    setAuditLoading(true);
    setAuditError(null);
    setAuditData(null);
    try {
      const folderResp = await api.readGdriveFolder({ url_or_id: folderInput });
      // The Hyrule gdrive tool returns HTTP 200 with { error, folder_id } on a
      // listing failure (bad/inaccessible folder, Drive/auth error) rather than
      // throwing. Surface that real error instead of letting it fall through to
      // `files || []` → a misleading "found 0 files". (Andrew, #183 review.)
      if (folderResp.error) {
        setAuditError(
          `Couldn't read that folder: ${folderResp.error}` +
          (folderResp.folder_id ? ` (folder ${folderResp.folder_id})` : '')
        );
        return;
      }
      const allFiles = folderResp.files || [];
      // Filter to readable doc types — PDFs, Docs, Slides, plain text
      const supported = allFiles.filter(f => {
        const mt = (f.mimeType || '').toLowerCase();
        return (
          mt.includes('pdf') ||
          mt.includes('document') ||
          mt.includes('presentation') ||
          mt === 'text/plain'
        );
      });
      if (supported.length === 0) {
        setAuditError(`No supported files in folder (found ${allFiles.length} total; need PDF / Doc / Slides / TXT)`);
        return;
      }
      const ids = supported.map(f => f.id);
      const coverage = await api.checkIngestionCoverage({
        source_document_ids: ids,
        namespace: 'market-research',
      });
      const filesById = Object.fromEntries(supported.map(f => [f.id, f]));
      setAuditData({
        ...coverage,
        folder_url: folderInput,
        folder_file_count: allFiles.length,
        supported_file_count: supported.length,
        files_by_id: filesById,
      });
    } catch (e) {
      console.error('Audit check failed:', e);
      // FastAPI 422 detail can be an array/object; coerce to string so React
      // can render it (rendering a non-string child throws).
      const detail = e?.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : detail
          ? JSON.stringify(detail)
          : null;
      setAuditError(detailStr || e.message || 'Audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  // Derive per-year coverage from ingested per_id_metadata + missing filenames.
  // Best-effort year parse on missing files (YYYY in name) since they have no
  // index metadata. Returns sorted year rows [{year, ingested, missing, total}].
  const auditPerYear = useMemo(() => {
    if (!auditData) return [];
    const yearMap = {};
    const bump = (year, key) => {
      if (!year) return;
      if (!yearMap[year]) yearMap[year] = { year, ingested: 0, missing: 0 };
      yearMap[year][key] += 1;
    };
    (auditData.ingested || []).forEach(id => {
      const meta = auditData.per_id_metadata?.[id];
      bump(meta?.document_year || null, 'ingested');
    });
    (auditData.missing || []).forEach(id => {
      const name = auditData.files_by_id?.[id]?.name || '';
      const match = name.match(/(20\d{2})/);
      bump(match ? parseInt(match[1], 10) : null, 'missing');
    });
    return Object.values(yearMap)
      .map(r => ({ ...r, total: r.ingested + r.missing }))
      .sort((a, b) => a.year - b.year);
  }, [auditData]);

  // ── Filter handlers ──
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };
  const clearFilters = () => {
    setFilters({ source: '', source_tier: '', report_type: '', document_year: '', geographic_focus: '', topics: '', vertical: '', platform: '', status: 'Embedded' });
    setPage(0);
  };
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && k !== 'status').length;

  const toggleSection = (s) => setCollapsedSections(prev => ({ ...prev, [s]: !prev[s] }));

  // ── Click bar → open browse in new tab (preserves dashboard) ──
  const openBrowseNewTab = (params) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'browse');
    Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleBarClick = (data) => {
    if (!data?.yearNum) return;
    const yr = data.yearNum;
    if (data.documents <= 10) {
      // Small year: show inline preview
      setDashYearPreview({ year: yr, loading: true });
      api.listReports({ document_year: yr, limit: 10, status: 'Embedded' }).then(res => {
        setDashYearPreview({ year: yr, docs: res?.results || [], loading: false });
      }).catch(() => setDashYearPreview(null));
    } else {
      openBrowseNewTab({ year: yr.toString() });
    }
  };

  // ── Stats cards data ──
  const statsCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Total Reports', value: stats.unique_documents || 0, icon: FiFileText },
      { label: 'Sources', value: stats.source_count || 0, icon: FiDatabase },
      { label: 'Years', value: stats.year_range || 'N/A', icon: FiCalendar },
      { label: 'Vectors', value: (stats.total_vectors || 0).toLocaleString(), icon: FiBarChart2 },
    ];
  }, [stats]);

  // ── Report type chart data ──
  const typeChartData = useMemo(() => {
    if (!stats) return [];
    // Use report_type_breakdown from Sheet-backed stats (with real counts)
    if (stats.report_type_breakdown?.length) {
      return stats.report_type_breakdown.map((rt, i) => ({
        name: rt.name, value: rt.count, fill: CHART_COLORS[i % CHART_COLORS.length],
      }));
    }
    // Fallback: just names (value=1)
    return (stats.report_types || []).map((rt, i) => ({
      name: rt, value: 1, fill: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [stats]);

  // ── Timeline chart data (enriched with source preview) ──
  const timelineChartData = useMemo(() => {
    return timeline.map(t => ({
      year: t.year.toString(),
      yearNum: t.year,
      documents: t.total_documents,
      sources: t.sources || [],
    }));
  }, [timeline]);

  // ── Top publishers chart data (from /sources endpoint) ──
  const topPublishersData = useMemo(() => {
    if (!sourcesData) return [];
    const all = [];
    for (const [key, val] of Object.entries(sourcesData)) {
      if (Array.isArray(val)) {
        val.forEach(p => all.push({ ...p, tier: key.includes('1') ? 1 : key.includes('2') ? 2 : 3 }));
      }
    }
    return all.sort((a, b) => b.documents - a.documents).slice(0, 15);
  }, [sourcesData]);


  // ── Tier badge with info tooltip ──
  const TIER_INFO = {
    1: { label: 'Tier 1', desc: 'Investment Banks & Big4 Consulting', examples: 'Goldman Sachs, J.P. Morgan, Morgan Stanley, Deloitte, Bain, McKinsey, PwC, BCG' },
    2: { label: 'Tier 2', desc: 'Specialized Research & Data Platforms', examples: 'Newzoo, Sensor Tower, PitchBook, Drake Star, DDM, VGI, AppMagic, Konvoy' },
    3: { label: 'Tier 3', desc: 'Independent Analysts & Media', examples: 'John Wright, Mishka Katkoff, Economist, Financial Times, Doug Shapiro' },
  };

  const [tierTooltip, setTierTooltip] = useState(null);
  const [tierFlipDown, setTierFlipDown] = useState(false);

  const handleTierEnter = (tier, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTierFlipDown(rect.top < 200);
    setTierTooltip(tier);
  };

  const TierBadge = ({ tier, showInfo = false }) => (
    <span className="tier-badge-wrapper">
      <span className="tier-badge" style={{ backgroundColor: TIER_COLORS[tier] || '#94a3b8' }}>
        T{tier}
      </span>
      {showInfo && (
        <span
          className="tier-info-icon"
          onMouseEnter={(e) => handleTierEnter(tier, e)}
          onMouseLeave={() => setTierTooltip(null)}
        >
          <FiInfo size={12} />
          {tierTooltip === tier && (
            <div className={`tier-tooltip${tierFlipDown ? ' flip-down' : ''}`}>
              <div className="tier-tooltip-title">{TIER_INFO[tier]?.label} — {TIER_INFO[tier]?.desc}</div>
              <div className="tier-tooltip-examples">Examples: {TIER_INFO[tier]?.examples}</div>
            </div>
          )}
        </span>
      )}
    </span>
  );

  // ── Review handlers (human-in-the-loop) ──
  const REVIEW_STATUSES = {
    raw: { label: 'Raw', className: 'review-raw' },
    confirmed: { label: 'Confirmed', icon: FiCheckCircle, className: 'review-confirmed' },
    flagged: { label: 'Flagged', icon: FiAlertTriangle, className: 'review-flagged' },
    dismissed: { label: 'Dismissed', icon: FiX, className: 'review-dismissed' },
  };

  const handleReviewStatus = async (fileId, newStatus, rowIdx) => {
    if (reviewSaving) return;
    setReviewSaving(true);
    try {
      await api.knowledge.updateReportReview(fileId, { status: newStatus });
      setReports(prev => prev.map(r =>
        r.file_id === fileId ? { ...r, review_status: newStatus } : r
      ));
    } catch (err) {
      console.error('Failed to update review status:', err);
    } finally {
      setReviewSaving(false);
    }
  };

  const handleReviewNote = async (fileId) => {
    if (reviewSaving || !reviewNoteInput.trim()) return;
    setReviewSaving(true);
    try {
      await api.knowledge.updateReportReview(fileId, { note: reviewNoteInput.trim() });
      setReports(prev => prev.map(r =>
        r.file_id === fileId ? { ...r, reviewer_note: reviewNoteInput.trim() } : r
      ));
      setShowReviewNote(false);
    } catch (err) {
      console.error('Failed to save review note:', err);
    } finally {
      setReviewSaving(false);
    }
  };

  // ── Flag handler (search result data quality issues → Feedback Sheet) ──
  const handleFlag = async () => {
    if (!flagForm || !flagText.trim() || flagSaving) return;
    setFlagSaving(true);
    try {
      await api.flagIssue({
        entity: flagForm.source || 'Unknown',
        field: flagField,
        issue: flagText.trim(),
        file_id: flagForm.file_id || null,
        chunk_id: flagForm.chunk_id ?? null,
        requester: 'platform_ui',
      });
      setFlagSuccess(flagForm.id);
      setFlagForm(null);
      setFlagText('');
      // flagSuccess auto-clears via useEffect cleanup (handles unmount safely)
    } catch (err) {
      console.error('Flag failed:', err);
    } finally {
      setFlagSaving(false);
    }
  };

  // ═════════════════════════════════════════════════════════════
  // ── Intel Tooltip helper ──
  const IntelTip = ({ id, text, children }) => (
    <span
      className="intel-tip-trigger"
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setIntelTooltip({ id, text, x: rect.left + rect.width / 2, y: rect.top });
      }}
      onMouseLeave={() => setIntelTooltip(null)}
    >
      {children}
    </span>
  );

  // RENDER
  // ═════════════════════════════════════════════════════════════

  return (
    <div className="knowledge-base-page workbench-mode">
      {/* Global intel tooltip */}
      {intelTooltip && (
        <div className="intel-tooltip-popup" style={{
          left: Math.min(intelTooltip.x, window.innerWidth - 400),
          top: intelTooltip.y - 10,
          transform: 'translate(-50%, -100%)',
        }}>
          {intelTooltip.text}
        </div>
      )}
      {/* ── Header ── */}
      <div className="kb-header">
        <div className="kb-header-left">
          <FiBook size={24} />
          <div>
            <h1>Knowledge Base</h1>
            <p className="page-subtitle">
              {stats ? `${stats.unique_documents || 0} reports from ${stats.source_count || 0} sources` : 'Loading...'}
            </p>
          </div>
        </div>
        <div className="kb-header-actions">
          <button
            className={`kb-tab ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            <FiDatabase size={16} /> Browse
          </button>
          <button
            className={`kb-tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            <FiSearch size={16} /> Search
          </button>
          <button
            className={`kb-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <FiBarChart2 size={16} /> Dashboard
          </button>
          <button
            className={`kb-tab ${activeTab === 'intelligence' ? 'active' : ''}`}
            onClick={() => setActiveTab('intelligence')}
          >
            <FiGlobe size={16} /> Source Intelligence
          </button>
          <button
            className={`kb-tab ${activeTab === 'validation' ? 'active' : ''}`}
            onClick={() => setActiveTab('validation')}
          >
            <FiCheckCircle size={16} /> Cross-Validation
          </button>
          <button
            className={`kb-tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <FiPieChart size={16} /> Ingestion Audit
          </button>
          <button className="methodology-btn" onClick={() => setShowMethodology(true)} title="Methodology & Glossary">
            <FiInfo size={16} />
          </button>
        </div>
      </div>

      {/* ── Search Bar (always visible) ── */}
      <form className="kb-search-bar" onSubmit={handleSearch}>
        <FiSearch size={18} className="search-icon" />
        <input
          type="text"
          placeholder="Search market research reports... (e.g., 'mobile gaming M&A trends in APAC')"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button type="button" className="clear-btn" onClick={() => { setSearchQuery(''); setSearchResults(null); setActiveTab('browse'); }}>
            <FiX size={16} />
          </button>
        )}
        <div className="search-mode-toggle">
          {[
            { key: 'hybrid', label: 'Hybrid', hint: 'Semantic + Keywords' },
            { key: 'semantic', label: 'Semantic', hint: 'Conceptual meaning' },
            { key: 'lexical', label: 'Lexical', hint: 'Exact keywords' },
          ].map(m => (
            <button
              key={m.key}
              type="button"
              className={`search-mode-btn ${searchMode === m.key ? 'active' : ''}`}
              onClick={() => setSearchMode(m.key)}
              title={m.hint}
            >
              {m.label}
            </button>
          ))}
        </div>
        <button type="submit" className="search-submit" disabled={searchLoading}>
          {searchLoading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {/* ── Filter Bar ── */}
      <div className="kb-filter-bar">
        <button className="filter-toggle" onClick={() => setFiltersOpen(!filtersOpen)}>
          <FiFilter size={16} />
          Filters {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
        </button>
        {activeFilterCount > 0 && (
          <button className="clear-filters" onClick={clearFilters}>Clear all</button>
        )}
        {/* Active filter chips */}
        <div className="filter-chips">
          {Object.entries(filters).filter(([k, v]) => v && k !== 'status').map(([key, value]) => (
            <span key={key} className="filter-chip">
              {key.replace('_', ' ')}: {value}
              <FiX size={12} onClick={() => handleFilterChange(key, '')} />
            </span>
          ))}
        </div>
      </div>

      {/* ── Filter Panel (collapsible) ── */}
      {filtersOpen && filterOptions && (
        <div className="kb-filter-panel">
          <div className="filter-grid">
            <div className="filter-group">
              <label>Source</label>
              <select value={filters.source} onChange={(e) => handleFilterChange('source', e.target.value)}>
                <option value="">All sources</option>
                {(filterOptions.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>
                Source Tier
                <span
                  className="tier-info-inline"
                  onMouseEnter={() => setTierTooltip('filter')}
                  onMouseLeave={() => setTierTooltip(null)}
                >
                  <FiInfo size={11} />
                  {tierTooltip === 'filter' && (
                    <div className="tier-tooltip">
                      <div className="tier-tooltip-row"><strong>T1 — IB / Big4:</strong> Goldman Sachs, J.P. Morgan, Deloitte, Bain, McKinsey</div>
                      <div className="tier-tooltip-row"><strong>T2 — Specialized:</strong> Newzoo, Sensor Tower, PitchBook, Drake Star, VGI</div>
                      <div className="tier-tooltip-row"><strong>T3 — Independent:</strong> John Wright, Economist, Financial Times</div>
                    </div>
                  )}
                </span>
              </label>
              <select value={filters.source_tier} onChange={(e) => handleFilterChange('source_tier', e.target.value)}>
                <option value="">All tiers</option>
                <option value="1">Tier 1 - IB / Big4 / Consulting</option>
                <option value="2">Tier 2 - Specialized / Data</option>
                <option value="3">Tier 3 - Independent / Media</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Report Type</label>
              <select value={filters.report_type} onChange={(e) => handleFilterChange('report_type', e.target.value)}>
                <option value="">All types</option>
                {(filterOptions.report_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Year</label>
              <select value={filters.document_year} onChange={(e) => handleFilterChange('document_year', e.target.value)}>
                <option value="">All years</option>
                {(filterOptions.years || []).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Geography</label>
              <select value={filters.geographic_focus} onChange={(e) => handleFilterChange('geographic_focus', e.target.value)}>
                <option value="">All regions</option>
                {(filterOptions.geographic_focus || []).map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Topic</label>
              <select value={filters.topics} onChange={(e) => handleFilterChange('topics', e.target.value)}>
                <option value="">All topics</option>
                {(filterOptions.topics || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Platform</label>
              <select value={filters.platform} onChange={(e) => handleFilterChange('platform', e.target.value)}>
                <option value="">All platforms</option>
                {(filterOptions.platforms || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Vertical</label>
              <select value={filters.vertical} onChange={(e) => handleFilterChange('vertical', e.target.value)}>
                <option value="">All verticals</option>
                {(filterOptions.verticals || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* TAB: DASHBOARD                                         */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'dashboard' && (
        <div className="kb-dashboard">
          {/* Stats Cards */}
          <div className="stats-cards">
            {statsCards.map((card, i) => (
              <div key={i} className="stat-card">
                <card.icon size={20} className="stat-icon" />
                <div className="stat-value">{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</div>
                <div className="stat-label">{card.label}</div>
              </div>
            ))}
          </div>

          {/* Charts Row 1: Timeline + Report Types */}
          <div className="charts-row">
            {/* Interactive Timeline Chart */}
            <div className="chart-card chart-card-wide">
              <h3>Reports by Year <span className="chart-hint">click a bar to explore</span></h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={timelineChartData} onClick={(e) => e?.activePayload?.[0]?.payload && handleBarClick(e.activePayload[0].payload)}>
                  <XAxis dataKey="year" fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                  <YAxis fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="dash-tooltip">
                        <div className="dash-tooltip-title">{d.year} — {d.documents} reports</div>
                        {(d.sources || []).slice(0, 5).map((s, i) => (
                          <div key={i} className="dash-tooltip-row">
                            <span className="dash-tooltip-src">{s.source}</span>
                            <span className="dash-tooltip-count">{s.documents}</span>
                          </div>
                        ))}
                        {d.sources?.length > 5 && <div className="dash-tooltip-more">+{d.sources.length - 5} more sources</div>}
                        <div className="dash-tooltip-action">{d.documents <= 10 ? 'Click to preview' : 'Click to browse'}</div>
                      </div>
                    );
                  }} />
                  <Bar dataKey="documents" fill="#1E3A8A" radius={[4, 4, 0, 0]} cursor="pointer" />
                </BarChart>
              </ResponsiveContainer>
              {/* Year preview panel (for small years) */}
              {dashYearPreview && (
                <div className="year-preview-panel">
                  <div className="year-preview-header">
                    <strong>{dashYearPreview.year} Reports</strong>
                    <div className="year-preview-actions">
                      <button className="year-preview-browse" onClick={() => { openBrowseNewTab({ year: dashYearPreview.year.toString() }); setDashYearPreview(null); }}>
                        Browse all <FiExternalLink size={12} />
                      </button>
                      <button
                        type="button"
                        className="year-preview-close"
                        onClick={() => setDashYearPreview(null)}
                        aria-label="Close year preview"
                      >
                        <FiX size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  {dashYearPreview.loading ? (
                    <div className="year-preview-loading">Loading...</div>
                  ) : (
                    <div className="year-preview-list">
                      {(dashYearPreview.docs || []).map((doc, i) => (
                        <div key={i} className="year-preview-item" onClick={() => openBrowseNewTab({ source: doc.source, year: dashYearPreview.year.toString() })}>
                          <span className={`tier-badge tier-${doc.source_tier}`}>T{doc.source_tier}</span>
                          <span className="year-preview-source">{doc.source}</span>
                          <span className="year-preview-type">{doc.report_type}</span>
                          <span className="year-preview-name" title={doc.file_name}>{(doc.file_name || '').slice(0, 50)}{(doc.file_name || '').length > 50 ? '...' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Report Types Horizontal Bar */}
            {typeChartData.length > 0 && (
              <div className="chart-card">
                <h3>Report Types <span className="chart-hint">click to browse</span></h3>
                <ResponsiveContainer width="100%" height={Math.max(280, typeChartData.length * 24)}>
                  <BarChart data={typeChartData} layout="vertical" margin={{ left: 120, right: 20 }}
                    onClick={(e) => {
                      const rt = e?.activePayload?.[0]?.payload?.name;
                      if (rt) openBrowseNewTab({ type: rt });
                    }}
                  >
                    <XAxis type="number" fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                    <YAxis type="category" dataKey="name" fontSize={11} width={120} tick={{ fill: 'var(--text-primary, #fff)' }} />
                    <Tooltip formatter={(value) => [`${value} reports`]} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer">
                      {typeChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Charts Row 2: Top Publishers + Tier Composition */}
          <div className="charts-row">
            {/* Top Publishers Horizontal Bar */}
            {topPublishersData.length > 0 && (
              <div className="chart-card chart-card-wide">
                <h3>Top Publishers <span className="chart-hint">by report count — click to browse</span></h3>
                <ResponsiveContainer width="100%" height={Math.max(280, topPublishersData.length * 28)}>
                  <BarChart data={topPublishersData} layout="vertical" margin={{ left: 100 }}
                    onClick={(e) => {
                      const src = e?.activePayload?.[0]?.payload?.source;
                      if (src) openBrowseNewTab({ source: src });
                    }}
                  >
                    <XAxis type="number" fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                    <YAxis type="category" dataKey="source" fontSize={11} width={100} tick={{ fill: 'var(--text-primary, #fff)' }} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="dash-tooltip">
                          <div className="dash-tooltip-title">{d.source}</div>
                          <div className="dash-tooltip-row"><span>Documents</span><span>{d.documents}</span></div>
                          <div className="dash-tooltip-row"><span>Chunks</span><span>{d.chunks?.toLocaleString()}</span></div>
                          <div className="dash-tooltip-action">Click to browse</div>
                        </div>
                      );
                    }} />
                    <Bar dataKey="documents" radius={[0, 4, 4, 0]} cursor="pointer">
                      {topPublishersData.map((d, i) => (
                        <Cell key={i} fill={d.tier === 1 ? '#1E3A8A' : d.tier === 2 ? '#3b82f6' : '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="chart-legend-inline">
                  <span className="chart-legend-dot" style={{ background: '#1E3A8A' }} /> Tier 1 — IB / Big4
                  <span className="chart-legend-dot" style={{ background: '#3b82f6' }} /> Tier 2 — Specialized
                  <span className="chart-legend-dot" style={{ background: '#94a3b8' }} /> Tier 3 — Independent
                </div>
              </div>
            )}

            {/* Tier Composition Stacked Bar (from Source Intelligence) */}
            {tierComposition?.composition?.length > 0 && (
              <div className="chart-card">
                <h3>Tier Composition by Year</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={tierComposition.composition.filter(c => c.year >= 2019)}>
                    <XAxis dataKey="year" fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                    <YAxis fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="dash-tooltip">
                          <div className="dash-tooltip-title">{d.year} — {d.total} reports</div>
                          <div className="dash-tooltip-row"><span style={{ color: '#1E3A8A' }}>Tier 1 IB/Big4</span><span>{d.tier_1} ({d.tier_1_pct}%)</span></div>
                          <div className="dash-tooltip-row"><span style={{ color: '#3b82f6' }}>Tier 2 Specialized</span><span>{d.tier_2}</span></div>
                          <div className="dash-tooltip-row"><span style={{ color: '#94a3b8' }}>Tier 3 Independent</span><span>{d.tier_3}</span></div>
                        </div>
                      );
                    }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="tier_1" stackId="tier" fill="#1E3A8A" name="Tier 1" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="tier_2" stackId="tier" fill="#3b82f6" name="Tier 2" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="tier_3" stackId="tier" fill="#94a3b8" name="Tier 3" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Source Directory (by tier, with counts) */}
          {sourcesData && (
            <div className="workbench-section">
              <div className="section-header" onClick={() => toggleSection('sources')}>
                <div className="section-title">
                  <FiDatabase size={20} />
                  <h2>Source Directory ({sourcesData.total_sources} publishers)</h2>
                </div>
                {collapsedSections.sources ? <FiChevronDown size={20} /> : <FiChevronUp size={20} />}
              </div>
              {!collapsedSections.sources && (
                <div className="source-directory">
                  {[
                    { key: 'Tier 1 \u2014 IB / Big4 / Consulting', label: 'Tier 1 — IB / Big4 / Consulting', color: '#1E3A8A' },
                    { key: 'Tier 2 \u2014 Specialized / Data', label: 'Tier 2 — Specialized / Data', color: '#3b82f6' },
                    { key: 'Tier 3 \u2014 Independent / Media', label: 'Tier 3 — Independent / Media', color: '#94a3b8' },
                  ].map(tier => {
                    const publishers = sourcesData[tier.key] || [];
                    if (!publishers.length) return null;
                    return (
                      <div key={tier.key} className="source-tier-group">
                        <div className="source-tier-header">
                          <span className="source-tier-dot" style={{ background: tier.color }} />
                          {tier.label} ({publishers.length})
                        </div>
                        <div className="source-chips">
                          {publishers.map(p => (
                            <button key={p.source} className="source-chip" onClick={() => openBrowseNewTab({ source: p.source })}>
                              {p.source} <span className="source-chip-count">{p.documents}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* TAB: BROWSE                                            */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'browse' && (
        <div className="kb-browse">
          <div className="browse-header">
            <span className="result-count">
              {loading ? 'Loading...' : `${totalReports} reports`}
            </span>
            <div className="pagination">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span>Page {page + 1} of {Math.ceil(totalReports / PAGE_SIZE) || 1}</span>
              <button disabled={(page + 1) * PAGE_SIZE >= totalReports} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>

          <div className="kb-table-container">
            <table className="kb-table">
              <thead>
                <tr>
                  {[
                    { key: 'file_name', label: 'Report' },
                    { key: 'source', label: 'Source' },
                    { key: 'report_type', label: 'Type' },
                    { key: 'topics', label: 'Topics', sortable: false },
                    { key: 'document_year', label: 'Year' },
                    { key: 'geographic_focus', label: 'Geography', sortable: false },
                  ].map(col => {
                    const isSortable = col.sortable !== false;
                    const isActive = sortBy === col.key && isSortable;
                    const ariaSort = !isSortable ? 'none' : (isActive ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none');
                    const handleSort = () => {
                      if (!isSortable) return;
                      if (sortBy === col.key) {
                        setSortDir(d => d === 'desc' ? 'asc' : 'desc');
                      } else {
                        setSortBy(col.key);
                        setSortDir('desc');
                      }
                      setPage(0);
                    };
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        aria-sort={ariaSort}
                        className={isSortable ? 'sortable-th' : ''}
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            className="sortable-th-button"
                            onClick={handleSort}
                          >
                            {col.label}
                            {isActive && (
                              <span className="sort-indicator" aria-hidden="true">{sortDir === 'desc' ? ' ▾' : ' ▴'}</span>
                            )}
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    );
                  })}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r, i) => (
                  <tr key={r.file_id || i}>
                    <td className="name-cell">
                      <div className="report-name">{r.file_name}</div>
                      {r.summary && <div className="report-summary">{r.summary.substring(0, 120)}...</div>}
                    </td>
                    <td>
                      <div className="source-cell">
                        <TierBadge tier={r.source_tier} />
                        <span>{r.source}</span>
                        <button
                          className="row-info-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMetadataPopup(metadataPopup?.id === r.file_id ? null : { id: r.file_id, data: r });
                          }}
                          title="View report metadata"
                        >
                          <FiInfo size={13} />
                        </button>
                      </div>
                      {metadataPopup?.id === r.file_id && (
                        <div className="report-metadata-popup" onClick={(e) => e.stopPropagation()}>
                          <div className="metadata-section">
                            <div className="metadata-label">Personality</div>
                            <div><strong>Source:</strong> {r.source} ({TIER_LABELS[r.source_tier] || `Tier ${r.source_tier}`})</div>
                            <div><strong>Topics:</strong> {(r.topics || []).join(', ') || 'N/A'}</div>
                            {r.summary && <div className="metadata-summary">{r.summary}</div>}
                          </div>
                          <div className="metadata-section">
                            <div className="metadata-label">Energy</div>
                            <div><strong>Report Type:</strong> {r.report_type || 'N/A'}</div>
                            <div><strong>Format:</strong> {r.document_format || 'PDF Report'}</div>
                          </div>
                          <div className="metadata-section">
                            <div className="metadata-label">Matter</div>
                            <div><strong>Vertical:</strong> {r.vertical || 'N/A'}</div>
                            <div><strong>Platform:</strong> {r.platform || 'N/A'}</div>
                          </div>
                          <div className="metadata-section">
                            <div className="metadata-label">Space & Time</div>
                            <div><strong>Geography:</strong> {(r.geographic_focus || []).join(', ') || 'N/A'}</div>
                            <div><strong>Year:</strong> {r.document_year || 'N/A'}{r.time_period ? ` (${r.time_period})` : ''}</div>
                          </div>
                          <div className="metadata-section">
                            <div className="metadata-label">System</div>
                            <div><strong>Chunks:</strong> {r.chunks || 0} | <strong>Vectors:</strong> {r.vectors || 0}</div>
                            <div><strong>Status:</strong> {r.status || 'N/A'}</div>
                          </div>
                          <div className="metadata-section">
                            <div className="metadata-label">Tier Explanation</div>
                            <div>{TIER_INFO[r.source_tier]?.desc || 'Unknown'}</div>
                            <div className="metadata-summary">Examples: {TIER_INFO[r.source_tier]?.examples || 'N/A'}</div>
                          </div>
                          {/* ── Review Controls (human-in-the-loop) ── */}
                          <div className="metadata-section metadata-review">
                            <div className="metadata-label">Review</div>
                            {r.reviewer_note && (
                              <div className="review-existing-note">
                                <FiInfo size={11} /> {r.reviewer_note}
                              </div>
                            )}
                            <div className="review-status-buttons">
                              {Object.entries(REVIEW_STATUSES).map(([key, conf]) => (
                                <button
                                  key={key}
                                  className={`review-status-btn ${conf.className} ${(r.review_status || 'raw') === key ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleReviewStatus(r.file_id, key); }}
                                  disabled={reviewSaving}
                                  title={conf.label}
                                >
                                  {conf.icon && <conf.icon size={11} />}
                                  {conf.label}
                                </button>
                              ))}
                              <button
                                className={`review-note-toggle ${showReviewNote ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowReviewNote(!showReviewNote);
                                  if (!showReviewNote) setReviewNoteInput(r.reviewer_note || '');
                                }}
                                title="Add note"
                              >
                                <FiInfo size={13} />
                              </button>
                            </div>
                            {showReviewNote && (
                              <div className="review-note-input-row">
                                <input
                                  type="text"
                                  className="review-note-input"
                                  placeholder="e.g. Wrong tier, should be Tier 2..."
                                  value={reviewNoteInput}
                                  onChange={(e) => setReviewNoteInput(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleReviewNote(r.file_id)}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                                <button
                                  className="review-note-submit"
                                  onClick={(e) => { e.stopPropagation(); handleReviewNote(r.file_id); }}
                                  disabled={reviewSaving || !reviewNoteInput.trim()}
                                >
                                  Save
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                    <td><span className="type-badge">{r.report_type}</span></td>
                    <td>
                      <div className="topics-cell">
                        {(r.topics || []).slice(0, 2).map(t => <span key={t} className="topic-tag">{t}</span>)}
                        {(r.topics || []).length > 2 && <span className="topic-more">+{r.topics.length - 2}</span>}
                      </div>
                    </td>
                    <td>{r.document_year || '—'}</td>
                    <td>{(r.geographic_focus || []).join(', ') || '—'}</td>
                    <td>
                      {r.file_url && (
                        <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="open-link" title="Open in GDrive">
                          <FiExternalLink size={16} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && reports.length === 0 && (
                  <tr><td colSpan={7} className="empty-state">No reports match your filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* TAB: SEARCH RESULTS                                    */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'search' && (
        <div className="kb-search-results">
          {searchLoading && <div className="loading-indicator">Searching knowledge base...</div>}

          {searchResults && !searchLoading && (
            <>
              {(() => {
                const scores = (searchResults.results || []).map(r => r.score);
                const mean = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
                const stdev = scores.length > 1 ? Math.sqrt(scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length) : 0;
                const threshold = Math.max(mean - 0.5 * stdev, 0.15); // floor at 15%
                const aboveCount = scores.filter(s => s >= threshold).length;

                return (
                  <div className="search-meta">
                    <span>{searchResults.total_results} results in {searchResults.elapsed_ms}ms{searchResults.mode ? ` (${searchResults.mode})` : ''}</span>
                    {scores.length > 0 && (
                      <span className="relevance-indicator">
                        <span className="relevance-strong">{aboveCount} relevant</span>
                        <span className="relevance-divider">&bull;</span>
                        <span className="relevance-stats">threshold {(threshold * 100).toFixed(0)}% (avg {(mean * 100).toFixed(0)}% &plusmn; {(stdev * 100).toFixed(0)}%)</span>
                      </span>
                    )}
                  </div>
                );
              })()}

              {searchResults.results?.map((r, i) => {
                // Relevance threshold (compute once, reuse)
                const allScores = (searchResults.results || []).map(x => x.score);
                const rMean = allScores.length ? allScores.reduce((a, b) => a + b, 0) / allScores.length : 0;
                const rStdev = allScores.length > 1 ? Math.sqrt(allScores.reduce((a, b) => a + Math.pow(b - rMean, 2), 0) / allScores.length) : 0;
                const rThreshold = Math.max(rMean - 0.5 * rStdev, 0.15);
                const isBelowThreshold = r.score < rThreshold;

                // Split contextual prefix from chunk content
                // Prefix is baked as first paragraph(s) before \n\n
                const textParts = (r.text || '').split('\n\n');
                const prefix = textParts.length > 1 ? textParts[0] : '';
                const chunkBody = textParts.length > 1 ? textParts.slice(1).join('\n\n') : textParts[0];

                return (
                  <div key={r.id || i} className={`search-result-card ${isBelowThreshold ? 'below-threshold' : ''}`}>
                    {/* Header: score + source + type + year + PDF link */}
                    <div className="result-header">
                      <div className={`result-score ${r.score >= 0.4 ? 'score-high' : r.score >= 0.25 ? 'score-mid' : 'score-low'}`}>
                        {(r.score * 100).toFixed(0)}%
                        <div className="score-bar"><div className="score-fill" style={{ width: `${Math.min(r.score * 100 / 0.6, 100)}%` }} /></div>
                      </div>
                      <div className="result-meta">
                        <TierBadge tier={r.source_tier} showInfo={true} />
                        <span className="result-source">{r.source}</span>
                        <span className="result-type">{r.report_type}</span>
                        {r.document_year > 0 && <span className="result-year">{r.document_year}</span>}
                        <span className="result-chunk-info">Chunk {r.chunk_id + 1}/{r.total_chunks}</span>
                      </div>
                      <div className="result-actions">
                        {/* View Report button */}
                        {r.file_id && (
                          <button
                            className="result-action-btn"
                            onClick={(e) => { e.stopPropagation(); openBrowseNewTab({ source: r.source }); }}
                            title="Browse this source's reports"
                          >
                            <FiBook size={13} /> Report
                          </button>
                        )}
                        {/* Flag button */}
                        <button
                          className={`result-action-btn flag-btn ${flagSuccess === r.id ? 'flag-success' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (flagForm?.id === r.id) { setFlagForm(null); return; }
                            setFlagForm({ id: r.id, source: r.source, file_id: r.file_id, chunk_id: r.chunk_id });
                            setFlagText('');
                            setFlagField('source');
                          }}
                          title="Flag data quality issue"
                        >
                          {flagSuccess === r.id ? <><FiCheckCircle size={13} /> Flagged</> : <><FiAlertTriangle size={13} /> Flag</>}
                        </button>
                        {/* Metadata popup trigger (click to toggle) */}
                        <button
                          className="metadata-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMetadataPopup(metadataPopup?.id === r.id ? null : { id: r.id });
                          }}
                          title="View PMEST metadata"
                        >
                          <FiInfo size={14} />
                          {metadataPopup?.id === r.id && (
                            <div className="metadata-popup">
                              <div className="metadata-section">
                                <div className="metadata-label">System</div>
                                <div>Chunk {r.chunk_id + 1} of {r.total_chunks}</div>
                                <div>Match: {r.match_type || 'hybrid'} ({(r.score * 100).toFixed(1)}%)</div>
                                {r.has_visual && <div>Contains visual data</div>}
                              </div>
                              <div className="metadata-section">
                                <div className="metadata-label">Personality</div>
                                <div><strong>Source:</strong> {r.source} (Tier {r.source_tier})</div>
                                <div><strong>Topics:</strong> {(r.topics || []).join(', ') || 'N/A'}</div>
                                {r.entities?.length > 0 && <div><strong>Entities:</strong> {r.entities.slice(0, 8).join(', ')}</div>}
                                {r.keywords?.length > 0 && <div><strong>Keywords:</strong> {r.keywords.slice(0, 6).join(', ')}</div>}
                              </div>
                              <div className="metadata-section">
                                <div className="metadata-label">Matter</div>
                                {r.transcend_vertical?.length > 0 && <div><strong>Vertical:</strong> {r.transcend_vertical.join(', ')}</div>}
                                {r.transcend_platform?.length > 0 && r.transcend_platform[0] !== 'NA' && <div><strong>Platform:</strong> {r.transcend_platform.join(', ')}</div>}
                                {r.transcend_tech_core?.length > 0 && r.transcend_tech_core[0] !== 'NA' && <div><strong>Tech:</strong> {r.transcend_tech_core.join(', ')}</div>}
                                {r.transcend_segment?.length > 0 && r.transcend_segment[0] !== 'NA' && <div><strong>Segment:</strong> {r.transcend_segment.join(', ')}</div>}
                              </div>
                              <div className="metadata-section">
                                <div className="metadata-label">Energy</div>
                                <div><strong>Type:</strong> {r.report_type}</div>
                                {r.document_format && <div><strong>Format:</strong> {r.document_format}</div>}
                              </div>
                              <div className="metadata-section">
                                <div className="metadata-label">Space & Time</div>
                                <div><strong>Geography:</strong> {(r.geographic_focus || []).join(', ')}</div>
                                <div><strong>Year:</strong> {r.document_year || 'N/A'}{r.time_period ? ` (${r.time_period})` : ''}</div>
                              </div>
                              {r.report_summary && (
                                <div className="metadata-section">
                                  <div className="metadata-label">Report Summary</div>
                                  <div className="metadata-summary">{r.report_summary}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </button>
                        {r.file_url && (
                          <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="open-link" title="Open PDF in GDrive">
                            <FiExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Contextual prefix (if detected) */}
                    {prefix && (
                      <div className="result-prefix">{prefix}</div>
                    )}

                    {/* Chunk body (expandable) */}
                    <div className="result-text">
                      <div className={expandedChunks[r.id] ? '' : 'text-collapsed'}>
                        {chunkBody}
                      </div>
                    </div>
                    {chunkBody.length > 250 && (
                      <button
                        className="expand-btn"
                        onClick={() => setExpandedChunks(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      >
                        {expandedChunks[r.id] ? 'Collapse' : `Show full chunk (${chunkBody.length} chars)`}
                      </button>
                    )}

                    {/* Footer tags */}
                    <div className="result-footer">
                      {(r.topics || []).map(t => <span key={t} className="topic-tag small">{t}</span>)}
                      {(r.geographic_focus || []).filter(g => g !== 'Global').map(g => (
                        <span key={g} className="geo-tag">{g}</span>
                      ))}
                      {r.has_visual && <span className="visual-tag">Has charts</span>}
                    </div>

                    {/* Inline flag form */}
                    {flagForm?.id === r.id && (
                      <div className="flag-form">
                        <div className="flag-form-header">
                          <FiAlertTriangle size={14} /> Flag issue with <strong>{r.source}</strong> chunk
                          <button
                            type="button"
                            className="flag-form-close"
                            onClick={() => setFlagForm(null)}
                            aria-label="Close flag form"
                          >
                            <FiX size={14} aria-hidden="true" />
                          </button>
                        </div>
                        <div className="flag-form-row">
                          <select value={flagField} onChange={(e) => setFlagField(e.target.value)} className="flag-field-select">
                            <option value="source">Wrong Source</option>
                            <option value="classification">Bad Classification</option>
                            <option value="metadata">Incorrect Metadata</option>
                            <option value="content">Content Error</option>
                            <option value="duplicate">Duplicate</option>
                            <option value="other">Other</option>
                          </select>
                          <input
                            className="flag-input"
                            placeholder="Describe the issue (e.g. 'Source is Sensor Tower, not Google')"
                            value={flagText}
                            onChange={(e) => setFlagText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleFlag()}
                            autoFocus
                          />
                          <button className="flag-submit" onClick={handleFlag} disabled={flagSaving || !flagText.trim()}>
                            {flagSaving ? 'Saving...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {searchResults.total_results === 0 && (
                <div className="empty-state">No results found. Try a broader query or adjust filters.</div>
              )}
            </>
          )}

          {!searchResults && !searchLoading && (
            <div className="empty-state">
              <FiSearch size={32} />
              <p>Enter a query above to search across {stats?.unique_documents || '800+'} market research reports</p>
            </div>
          )}
        </div>
      )}
      {/* ═══════════════════════════════════════════════════════ */}
      {/* TAB: SOURCE INTELLIGENCE                               */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'intelligence' && (
        <div className="kb-intelligence">
          {intelLoading && <div className="loading-indicator">Loading source intelligence...</div>}

          {/* Section intro */}
          {!intelLoading && topicTrends && (
            <>
              <div className="intel-intro">
                <p>This page analyzes the <strong>behavior of the sources themselves</strong> — not what the reports say, but what the <em>patterns of reporting</em> reveal. When 12 independent publishers start covering the same topic, that's not noise — it's a validated market signal. When IBs increase coverage of a sector, advisory mandates follow 3-6 months later.</p>
                <p className="intel-intro-note">Hover <FiInfo size={12} style={{verticalAlign: 'middle'}} /> icons for context on why each signal matters.</p>
              </div>

              {/* Topic Trends Heatmap */}
              <div className="intel-section">
                <div className="intel-section-header">
                  <div>
                    <h3>Topic Momentum</h3>
                    <p className="intel-subtitle">Year-over-year coverage shifts reveal where institutional attention is moving</p>
                  </div>
                  <IntelTip id="section" text="Each number = how many report chunks mention this topic in that year. Rising topics attract investment capital. Dead topics (→0) signal thesis collapse. When 3+ Tier 1 sources converge on a topic, it's structural — not cyclical.">
                    <FiInfo size={16} />
                  </IntelTip>
                </div>
                <div className="intel-table-wrapper">
                  <table className="intel-table">
                    <thead>
                      <tr>
                        <th>Topic</th>
                        {(topicTrends.years || []).map(y => <th key={y} className="num">{y}</th>)}
                        <th>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(topicTrends.topics || []).map((t, i) => {
                        // Generate contextual insight per topic
                        const TOPIC_INSIGHTS = {
                          'AI & Technology': 'Exploded from near-zero to dominant coverage. Every tier is publishing. This is structural, not hype — coverage sustained at high base unlike Web3.',
                          'Web3': 'Coverage collapsed: peak → zero. Sources abandoned the thesis. When IBs stop writing, deal flow dried up 6-12 months earlier.',
                          'Esports': 'Same collapse pattern as Web3. No Tier 1 coverage remaining. Not an investable theme for institutional capital.',
                          'Mobile Gaming': 'After declining, quiet resurgence driven by casual-to-midcore evolution and mobile M&A (Scopely, Dream Games).',
                          'M&A & Deal Activity': 'High convergence = deal market is active. When 7+ sources publish deal tables simultaneously, mega-deals are 30-60 days behind.',
                          'Gaming Market': 'Baseline coverage — always present. Watch for acceleration or deceleration vs the baseline.',
                          'VC & Investment': 'Tracks capital deployment cycles. Rising = new funds deploying. Falling = dry powder accumulating.',
                          'Geographic/Regional': 'Attention shifting to emerging markets (India, SEA, MENA). When coverage of a region spikes, investment follows 6-12 months later.',
                          'Consumer & Demographics': 'Player behavior research. Stable coverage = mature understanding. Spikes = new demographic insight (Gen Alpha, Gen Z spending patterns).',
                          'Media & Entertainment': 'Transmedia convergence. Netflix, Disney entering gaming. Rising coverage = sector boundaries blurring.',
                          'Fund Operations': 'Internal VC operations coverage. Rising = industry maturation, more institutional processes.',
                          'XR': 'VR/AR coverage. Watch for Apple Vision Pro impact on coverage patterns.',
                          'Live Operations': 'LiveOps, monetization, retention. Niche but operationally critical.',
                        };
                        const insight = TOPIC_INSIGHTS[t.topic] || '';

                        return (
                          <tr key={i} className={`topic-row ${t.trend}`}>
                            <td className="topic-name">
                              {t.topic}
                              {insight && (
                                <IntelTip id={`topic-${i}`} text={insight}>
                                  <FiInfo size={11} />
                                </IntelTip>
                              )}
                            </td>
                            {(topicTrends.years || []).map(y => {
                              const val = t[y] || 0;
                              const maxVal = Math.max(...(topicTrends.topics || []).map(tt => tt[y] || 0));
                              const opacity = maxVal > 0 ? Math.max(0.1, val / maxVal) : 0;
                              return (
                                <td key={y} className="num heatmap-cell" style={{
                                  backgroundColor: `rgba(30, 58, 138, ${opacity})`,
                                  color: opacity > 0.5 ? '#fff' : 'var(--text-primary)',
                                }}>
                                  {val || ''}
                                </td>
                              );
                            })}
                            <td>
                              <IntelTip id={`trend-${i}`} text={
                                t.trend === 'rising' ? 'Coverage increasing — institutional attention growing. More sources publishing = more deal flow expected.' :
                                t.trend === 'falling' ? 'Coverage declining — sources are walking away from this thesis. When IBs stop writing, deal flow dried up 6-12 months earlier.' :
                                'Coverage stable — established topic, watch for inflection points. Stable ≠ dead — it means the market has priced in the thesis.'
                              }>
                                <span className={`trend-badge ${t.trend}`}>
                                  {t.trend === 'rising' ? '↑' : t.trend === 'falling' ? '↓' : '→'}
                                </span>
                              </IntelTip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tier Composition Chart */}
              {tierComposition && (
                <div className="intel-section">
                  <div className="intel-section-header">
                    <div>
                      <h3>Tier Composition Over Time</h3>
                      <p className="intel-subtitle">When IBs increase coverage, advisory mandates follow 3-6 months later</p>
                    </div>
                    <IntelTip id="section" text="Source tiers: T1 = Investment Banks & Big4 (Goldman, JPM, Deloitte, Bain, McKinsey). T2 = Specialized research (Newzoo, Sensor Tower, PitchBook). T3 = Independent analysts & media. When T1 share rises above 60%, institutional capital is actively deploying research resources — this precedes deal mandates by 3-6 months.">
                      <FiInfo size={16} />
                    </IntelTip>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={tierComposition.composition || []}>
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="tier_1" name="Tier 1 (IB/Big4)" stackId="a" fill="#1E3A8A" />
                      <Bar dataKey="tier_2" name="Tier 2 (Specialized)" stackId="a" fill="#3b82f6" />
                      <Bar dataKey="tier_3" name="Tier 3 (Independent)" stackId="a" fill="#94a3b8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Entity Velocity */}
              {entityVelocity && (
                <div className="intel-section">
                  <div className="intel-section-header">
                    <div>
                      <h3>Entity Velocity</h3>
                      <p className="intel-subtitle">Cross-source mention frequency reveals strategic importance and deal activity</p>
                    </div>
                    <IntelTip id="section" text="Entities (companies, people, products) mentioned across multiple independent sources = validated strategic importance. High source spread (mentioned by 15+ sources) = consensus signal. Rising mention count for an entity = something is happening (deal, pivot, market shift). Individual names with high counts (e.g., Michael Metzger of Drake Star) are deal activity proxies.">
                      <FiInfo size={16} />
                    </IntelTip>
                  </div>
                  <div className="intel-table-wrapper">
                    <table className="intel-table">
                      <thead>
                        <tr>
                          <th>Entity</th>
                          <th className="num">Mentions</th>
                          <th className="num">Sources</th>
                          <th>Source Spread</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(entityVelocity.entities || []).map((e, i) => (
                          <tr key={i}>
                            <td><strong>{e.entity}</strong></td>
                            <td className="num">{e.mentions}</td>
                            <td className="num highlight">{e.source_count}</td>
                            <td className="source-list">{(e.sources || []).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Geographic Shifts */}
              {geoShifts && (
                <div className="intel-section">
                  <div className="intel-section-header">
                    <div>
                      <h3>Geographic Attention Shifts</h3>
                      <p className="intel-subtitle">Publisher coverage of regions is a leading indicator of capital flow</p>
                    </div>
                    <IntelTip id="section" text="When publishers increase coverage of a region, investment capital follows 6-12 months later. Watch for: T2 specialized sources (Lumikai for India, Niko IQ for China) leading coverage before T1 IBs arrive — that signals early-stage deal flow building. 'Global' is baseline; specific regions are the signal.">
                      <FiInfo size={16} />
                    </IntelTip>
                  </div>
                  <div className="intel-table-wrapper">
                    <table className="intel-table">
                      <thead>
                        <tr>
                          <th>Region</th>
                          {(geoShifts.years || []).map(y => <th key={y} className="num">{y}</th>)}
                          <th>Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(geoShifts.regions || []).map((r, i) => (
                          <tr key={i}>
                            <td>{r.geography}</td>
                            {(geoShifts.years || []).map(y => (
                              <td key={y} className="num">{r[y] || ''}</td>
                            ))}
                            <td>
                              <span className={`trend-badge ${r.trend}`}>
                                {r.trend === 'rising' ? '↑ Rising' : r.trend === 'falling' ? '↓ Falling' : '→ Stable'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Topic Convergence */}
              {convergence && (
                <div className="intel-section">
                  <div className="intel-section-header">
                    <div>
                      <h3>Topic Convergence ({convergence.year})</h3>
                      <p className="intel-subtitle">When multiple independent sources cover the same topic = validated signal</p>
                    </div>
                    <IntelTip id="section" text="Convergence = the number of independent publishers covering the same topic in the same year. High convergence (10+ sources) = market restructuring, not a trend. This is the strongest signal type — independent analysts reaching the same conclusion from different data means the signal is real. Compare convergence counts year-over-year to spot emerging vs established themes.">
                      <FiInfo size={16} />
                    </IntelTip>
                  </div>
                  <div className="convergence-grid">
                    {(convergence.convergence || []).slice(0, 8).map((c, i) => (
                      <div key={i} className="convergence-card">
                        <div className="convergence-count">{c.source_count}</div>
                        <div className="convergence-topic">{c.topic}</div>
                        <div className="convergence-sources">{(c.sources || []).join(', ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/*  CROSS-VALIDATION TAB                                  */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'validation' && (
        <div className="kb-validation">
          {valLoading && <div className="loading-indicator">Loading cross-validation data...</div>}

          {!valLoading && (valHeadlines || valClaims || valAgreement) && (
            <>
              {/* ── Overview Stats ── */}
              {valClaims && (
                <div className="val-overview-cards">
                  <div className="stat-card">
                    <div className="stat-value">{valClaims.total_matching || 0}</div>
                    <div className="stat-label">Data Claims</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{(valClaims.unique_sources || []).length}</div>
                    <div className="stat-label">Sources</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{(valClaims.unique_categories || []).length}</div>
                    <div className="stat-label">Categories</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{(valHeadlines?.metrics || []).length}</div>
                    <div className="stat-label">Validated Metrics</div>
                  </div>
                </div>
              )}

              {/* ── Headline Metric Cards (when validated metrics exist) ── */}
              {(valHeadlines?.metrics || []).length > 0 && (
                <div className="val-headlines">
                  {(valHeadlines.metrics || []).slice(0, 5).map((m, i) => {
                    // Quality Score is a legacy-compat display value derived FROM
                    // status, not an independent gradient — the canonical trust
                    // signal (and the badge/bar color) is `status` (TRA-413).
                    const qs = parseInt(m['Quality Score'] || 0);
                    const meta = getStatusMeta(m.Status);
                    return (
                      <div key={i} className="val-headline-card">
                        <div className="val-headline-category">{m['Metric Category'] || ''}</div>
                        <div className="val-headline-label">{m['Canonical Metric'] || ''}</div>
                        <div className="val-headline-value">{m['Weighted Value']} <span className="val-unit">{m.Unit || ''}</span></div>
                        {/* CI 95% Low/High retired here (TRA-478) — the backend's
                            Validated Metrics sheet mapping (validation_metrics.py::project_sheet)
                            no longer returns them; this always rendered nothing. */}
                        <div className="val-headline-meta">
                          <span className="val-quality-badge" style={{ background: meta.color }} title="Quality Score (legacy compat, derived from status)">Q{qs}</span>
                          <span className="val-source-count">{m['N Sources']} src</span>
                          <span className={`val-status-badge ${meta.className}`}>{meta.icon} {meta.label}</span>
                        </div>
                        <div className="val-quality-bar">
                          <div className="val-quality-fill" style={{ width: `${qs}%`, background: meta.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Metric Explorer ── */}
              {valMetrics && (valMetrics.metrics || []).length > 0 && (
                <div className="intel-section">
                  <h3>Metric Explorer</h3>
                  <p className="intel-subtitle">Cross-validated market data — click any row for source breakdown</p>

                  {/* Filters */}
                  <div className="val-filters">
                    <select
                      value={valFilters.category}
                      onChange={(e) => {
                        const f = { ...valFilters, category: e.target.value };
                        setValFilters(f);
                        fetchFilteredMetrics(f);
                      }}
                    >
                      <option value="">All Categories</option>
                      {(valMetrics.filter_options?.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                      value={valFilters.year}
                      onChange={(e) => {
                        const f = { ...valFilters, year: e.target.value };
                        setValFilters(f);
                        fetchFilteredMetrics(f);
                      }}
                    >
                      <option value="">All Years</option>
                      {(valMetrics.filter_options?.years || []).map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select
                      value={valFilters.min_quality}
                      onChange={(e) => {
                        const f = { ...valFilters, min_quality: e.target.value };
                        setValFilters(f);
                        fetchFilteredMetrics(f);
                      }}
                    >
                      {/* min_quality is a coarse pre-filter on the legacy-compat Quality
                          Score (still served + documented by GET /validation/metrics) —
                          not a trust banding. Filter by Status for the canonical signal. */}
                      <option value="">Any Quality Score</option>
                      <option value="90">Quality Score ≥90</option>
                      <option value="80">Quality Score ≥80</option>
                      <option value="70">Quality Score ≥70</option>
                    </select>
                    <select
                      value={valFilters.status}
                      onChange={(e) => {
                        const f = { ...valFilters, status: e.target.value };
                        setValFilters(f);
                        fetchFilteredMetrics(f);
                      }}
                    >
                      <option value="">All Statuses</option>
                      {(valMetrics.filter_options?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Table */}
                  <div className="intel-table-wrapper">
                    <table className="intel-table val-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Metric</th>
                          <th className="num">Value</th>
                          <th>Sources</th>
                          <th className="num">Q</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(valMetrics.metrics || []).map((m, i) => {
                          // See headline cards above — status is the canonical trust
                          // signal, Quality Score is legacy-compat display only (TRA-413).
                          const qs = parseInt(m['Quality Score'] || 0);
                          const meta = getStatusMeta(m.Status);
                          const isExpanded = valExpandedRow === i;
                          return (
                            <>
                              <tr
                                key={i}
                                className={`val-row ${isExpanded ? 'expanded' : ''}`}
                                onClick={() => {
                                  if (isExpanded) {
                                    setValExpandedRow(null);
                                    setValClaims(null);
                                  } else {
                                    setValExpandedRow(i);
                                    fetchClaimsForMetric(m['Metric Category'], m['Period Year']);
                                  }
                                }}
                              >
                                <td><span className="val-cat-chip">{m['Metric Category']}</span></td>
                                <td>
                                  <strong>{m['Canonical Metric']}</strong>
                                  {m['Canonical Scope'] && <div className="val-scope-hint">{m['Canonical Scope']}</div>}
                                </td>
                                <td className="num"><strong>{m['Weighted Value']}</strong> <span className="val-unit">{m.Unit}</span></td>
                                <td className="source-list">{m.Sources}</td>
                                <td className="num">
                                  <span className="val-qs-pill" style={{ background: meta.color }} title="Quality Score (legacy compat, derived from status)">{qs}</span>
                                </td>
                                <td>
                                  <span className={`val-status-badge ${meta.className}`}>
                                    {meta.icon} {meta.label}
                                  </span>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr key={`${i}-detail`} className="val-detail-row">
                                  <td colSpan={6}>
                                    <div className="val-detail-panel">
                                      {/* Quality Score Breakdown panel retired (TRA-478): it rendered
                                          Source Reliability / Validation Strength / Temporal Relevance /
                                          Methodology Transparency / Std Dev / CI 95% — none of which
                                          validation_metrics.py::project_sheet returns any more (it wrote
                                          the 4 sub-scores + Std Dev + CI as always-zero/blank, a
                                          scorecard-shaped fabrication). Quality Score itself is still
                                          live as a legacy-compat number (pill in the row above); see
                                          the Methodology tab for what it actually measures now. */}
                                      {/* Contributing Claims */}
                                      {valClaims && (valClaims.claims || []).length > 0 && (
                                        <div className="val-detail-section">
                                          <h4>Contributing Claims ({valClaims.total_matching} from {(valClaims.unique_sources || []).length} sources)</h4>
                                          <div className="val-claims-list">
                                            {(valClaims.claims || []).slice(0, 15).map((c, k) => (
                                              <div key={k} className="val-claim-row">
                                                <span className={`tier-badge tier-${c['Source Tier']}`}>T{c['Source Tier']}</span>
                                                <strong>{c.Source}</strong>
                                                <span className="val-claim-value">{c.Value} {c.Unit} {c.Change || ''}</span>
                                                <span className="val-claim-period">{c.Period}</span>
                                                <span className="val-claim-scope">{(c['Metric Scope (raw)'] || '').slice(0, 60)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="val-table-footer">{valMetrics.count} metrics · {valMetrics.total} total</div>
                </div>
              )}

              {/* ── Data Claims Explorer ── */}
              {valClaims && (valClaims.claims || []).length > 0 && (
                <div className="intel-section">
                  <h3>Data Claims ({valClaims.total_matching})</h3>
                  <p className="intel-subtitle">Quantitative data points extracted from market research reports — each claim is a single number from a single source</p>
                  <div className="val-filters">
                    <select onChange={async (e) => {
                      const result = await api.getValidationClaims({ category: e.target.value || undefined, limit: 50 });
                      setValClaims(result);
                    }}>
                      <option value="">All Categories</option>
                      {(valClaims.unique_categories || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select onChange={async (e) => {
                      const result = await api.getValidationClaims({ source: e.target.value || undefined, limit: 50 });
                      setValClaims(result);
                    }}>
                      <option value="">All Sources</option>
                      {(valClaims.unique_sources || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="intel-table-wrapper">
                    <table className="intel-table val-claims-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Metric</th>
                          <th className="num">Value</th>
                          <th>Change</th>
                          <th>Period</th>
                          <th>Source</th>
                          <th>Scope</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(valClaims.claims || []).map((c, i) => (
                          <tr key={i} className="claim-row-hover" title="">
                            <td><span className="val-cat-chip">{c['Metric Category']}</span></td>
                            <td><strong>{c['Metric Name']}</strong></td>
                            <td className="num"><strong>{c.Value}</strong> <span className="val-unit">{c.Unit}</span></td>
                            <td className="num">{c.Change || '—'}</td>
                            <td>{c.Period}</td>
                            <td>
                              <span className={`tier-badge tier-${c['Source Tier']}`}>T{c['Source Tier']}</span>
                              {' '}{c.Source}
                            </td>
                            <td className="val-scope-hint">
                              {(c['Metric Scope (raw)'] || '').slice(0, 60)}
                              {c.Context && (
                                <div className="claim-context-popup">
                                  <div className="claim-context-label">Original context:</div>
                                  {c.Context.slice(0, 300)}{c.Context.length > 300 ? '...' : ''}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="val-table-footer">
                    Showing {(valClaims.claims || []).length} of {valClaims.total_matching} claims
                  </div>
                </div>
              )}

              {/* ── Source Agreement Matrix ── */}
              {valAgreement && (valAgreement.grid || []).length > 0 && (
                <div className="intel-section">
                  <h3>Source Agreement Matrix</h3>
                  <p className="intel-subtitle">Which sources cover which metrics — green = has data, darker = more claims</p>
                  <div className="intel-table-wrapper">
                    <table className="intel-table val-matrix">
                      <thead>
                        <tr>
                          <th>Category</th>
                          {(valAgreement.sources || []).map(s => (
                            <th key={s} className="val-matrix-header">{s}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(valAgreement.grid || []).map((row, i) => (
                          <tr key={i}>
                            <td className="val-matrix-cat">{row.category}</td>
                            {(valAgreement.sources || []).map(src => {
                              const cell = row.sources?.[src] || {};
                              const count = cell.claim_count || 0;
                              const claims = cell.claims || [];
                              const opacity = count > 0 ? Math.min(1, 0.2 + count * 0.15) : 0;
                              const isHovered = matrixHover?.cat === row.category && matrixHover?.src === src;
                              return (
                                <td
                                  key={src}
                                  className="val-matrix-cell"
                                  style={{
                                    backgroundColor: count > 0 ? `rgba(30, 58, 138, ${opacity})` : 'transparent',
                                    color: opacity > 0.5 ? '#fff' : 'var(--text-primary)',
                                    position: 'relative',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (count > 0) {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setMatrixHover({ cat: row.category, src, claims, count, x: rect.left, y: rect.bottom + 4 });
                                    }
                                  }}
                                  onMouseLeave={() => setMatrixHover(null)}
                                >
                                  {count > 0 ? count : ''}
                                  {isHovered && claims.length > 0 && (
                                    <div className="matrix-hover-popup" style={{ left: 0, top: '100%' }}>
                                      <div className="matrix-hover-title">{src} — {row.category}</div>
                                      {claims.map((cl, ci) => (
                                        <div key={ci} className="matrix-hover-claim">
                                          <strong>{cl.value}</strong> {cl.unit} <span className="matrix-hover-period">{cl.period}</span>
                                          {cl.metric && <div className="matrix-hover-metric">{cl.metric}</div>}
                                        </div>
                                      ))}
                                      {count > 5 && <div className="matrix-hover-more">+{count - 5} more</div>}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Claim Clusters: Cross-Source Comparison ── */}
              {valClusters && (valClusters.clusters || []).length > 0 && (
                <div className="intel-section">
                  <h3>Cross-Source Comparison</h3>
                  <p className="intel-subtitle">Metrics measured by multiple independent sources — compare values, periods, and context side by side</p>
                  <div className="cluster-list">
                    {(valClusters.clusters || []).map((cluster, ci) => {
                      const isExpanded = valClusterExpanded === ci;
                      // Group claims by source for side-by-side
                      const bySource = {};
                      (cluster.claims || []).forEach(c => {
                        if (!bySource[c.source]) bySource[c.source] = [];
                        bySource[c.source].push(c);
                      });
                      return (
                        <div key={ci} className={`cluster-card ${isExpanded ? 'expanded' : ''}`}>
                          <div className="cluster-header" onClick={() => setValClusterExpanded(isExpanded ? null : ci)}>
                            <div className="cluster-meta">
                              <span className="val-cat-chip">{cluster.category}</span>
                              <strong className="cluster-title">{cluster.metric_name}</strong>
                              <span className="cluster-counts">{cluster.source_count} sources, {cluster.claim_count} claims</span>
                            </div>
                            <div className="cluster-sources-preview">
                              {cluster.sources.map(s => <span key={s} className="cluster-source-tag">{s}</span>)}
                            </div>
                            <FiChevronDown size={16} className={`cluster-chevron ${isExpanded ? 'rotated' : ''}`} />
                          </div>
                          {isExpanded && (
                            <div className="cluster-detail">
                              {Object.entries(bySource).map(([src, claims]) => (
                                <div key={src} className="cluster-source-group">
                                  <div className="cluster-source-name">
                                    <span className={`tier-badge tier-${claims[0]?.source_tier}`}>T{claims[0]?.source_tier}</span>
                                    {src}
                                  </div>
                                  <div className="cluster-claims">
                                    {claims.map((c, k) => (
                                      <div key={k} className="cluster-claim-row">
                                        <span className="cluster-claim-value"><strong>{c.value}</strong> {c.unit}</span>
                                        {c.change && <span className="cluster-claim-change">{c.change}</span>}
                                        <span className="cluster-claim-period">{c.period}</span>
                                        <span className="cluster-claim-geo">{c.geo}</span>
                                        {c.context && (
                                          <div className="cluster-claim-context">{c.context.slice(0, 150)}{c.context.length > 150 ? '...' : ''}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="val-table-footer">{valClusters.count} clusters with {'\u2265'}2 sources · {valClusters.total} total</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* ═══════════════════════════════════════════════════════ */}
      {/* INGESTION AUDIT TAB                                    */}
      {/* Paste a GDrive folder → coverage donut + per-year gap  */}
      {/* + missing-file list + cross-link to Cross-Validation.  */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'audit' && (
        <div className="kb-audit">
          <div className="audit-input-panel">
            <h2>Ingestion Audit</h2>
            <p className="audit-input-hint">
              Paste a Google Drive folder URL (or bare folder ID) to verify which files are ingested
              into the <code>market-research</code> knowledge index. Returns a coverage breakdown plus the
              specific files that are still missing.
            </p>
            <div className="audit-input-row">
              <input
                type="text"
                className="audit-input"
                placeholder="https://drive.google.com/drive/folders/... or folder ID"
                value={auditFolderUrl}
                onChange={(e) => setAuditFolderUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAuditCheck(); }}
                disabled={auditLoading}
              />
              <button
                type="button"
                className="audit-submit"
                onClick={handleAuditCheck}
                disabled={auditLoading || !auditFolderUrl.trim()}
              >
                {auditLoading ? 'Checking…' : 'Check coverage'}
              </button>
            </div>
            {auditError && (
              <div className="audit-error">
                <FiAlertTriangle size={14} /> {auditError}
              </div>
            )}
          </div>

          {auditData && (
            <>
              <div className="audit-stats-row">
                <div className="audit-stat">
                  <span className="audit-stat-label">Files in folder</span>
                  <span className="audit-stat-value">{auditData.folder_file_count}</span>
                  <span className="audit-stat-sub">{auditData.supported_file_count} supported (PDF / Doc / Slides / TXT)</span>
                </div>
                <div className="audit-stat audit-stat-good">
                  <span className="audit-stat-label">Ingested</span>
                  <span className="audit-stat-value">{auditData.ingested_count}</span>
                </div>
                <div className="audit-stat audit-stat-bad">
                  <span className="audit-stat-label">Missing</span>
                  <span className="audit-stat-value">{auditData.missing_count}</span>
                </div>
                <div className="audit-stat audit-stat-headline">
                  <span className="audit-stat-label">Coverage</span>
                  <span className="audit-stat-value">{auditData.coverage_pct.toFixed(1)}%</span>
                </div>
              </div>

              <div className="audit-charts-row">
                <div className="chart-card">
                  <h3>Coverage</h3>
                  {auditData.total_checked === 0 ? (
                    <p className="audit-empty">No supported files to check.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Ingested', value: auditData.ingested_count },
                            { name: 'Missing', value: auditData.missing_count },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={2}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#cbd5e1" />
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={24} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="chart-card chart-card-wide">
                  <h3>Coverage by year <span className="chart-hint">missing-year parsed from filenames</span></h3>
                  {auditPerYear.length === 0 ? (
                    <p className="audit-empty">No year metadata available.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={auditPerYear}>
                        <XAxis dataKey="year" fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                        <YAxis fontSize={11} tick={{ fill: 'var(--text-secondary, #888)' }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="ingested" stackId="cov" fill="#10b981" name="Ingested" />
                        <Bar dataKey="missing" stackId="cov" fill="#cbd5e1" name="Missing" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              {auditData.missing_count > 0 && (
                <div className="audit-gap-list">
                  <div className="audit-gap-header">
                    <h3>Missing from index ({auditData.missing_count})</h3>
                    <p className="audit-gap-hint">
                      These files exist in the folder but no chunks were found in the knowledge index. They
                      are candidates for <code>+ingest</code>.
                    </p>
                  </div>
                  <ul className="audit-gap-files">
                    {auditData.missing.map(id => {
                      const f = auditData.files_by_id?.[id];
                      const name = f?.name || id;
                      return (
                        <li key={id}>
                          <a
                            href={`https://drive.google.com/file/d/${id}/view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={id}
                          >
                            {name} <FiExternalLink size={11} />
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {auditData.ingested_count > 0 && (
                <div className="audit-cross-link">
                  <p>
                    The {auditData.ingested_count} ingested file{auditData.ingested_count === 1 ? '' : 's'} {auditData.ingested_count === 1 ? 'is' : 'are'} in the knowledge index. To compare
                    them against each other for cross-source claims, switch to the Cross-Validation tab.
                  </p>
                  <button type="button" onClick={() => setActiveTab('validation')}>
                    Compare these sources <FiExternalLink size={14} />
                  </button>
                </div>
              )}

              {auditData.truncated && (
                <div className="audit-warning">
                  <FiAlertTriangle size={14} /> One or more batches hit the per-batch
                  match ceiling. Some heavily-indexed files may be under-counted. Re-run with a smaller folder
                  if precise per-file chunk counts matter.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* METHODOLOGY & GLOSSARY MODAL                          */}
      {/* ═══════════════════════════════════════════════════════ */}
      {/* NOTE: tier weights, PMEST facet copy, and scoring specifics below are
          hardcoded to the frontend. They mirror the backend ranker config — if
          scoring thresholds are tuned on the Hyrule side, update this modal too
          (or replace this static copy with a GET /knowledge/methodology endpoint).
          Owner: whoever tunes the ranker (currently Fernando). */}
      {showMethodology && (
        <div className="methodology-overlay" onClick={() => setShowMethodology(false)}>
          <div
            className="methodology-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="methodology-heading"
          >
            <div className="methodology-header">
              <h2 id="methodology-heading">Methodology & Glossary</h2>
              <p>How we classify, score, and validate market intelligence</p>
              <button
                type="button"
                className="methodology-close"
                onClick={() => setShowMethodology(false)}
                aria-label="Close methodology dialog"
                autoFocus
              >
                <FiX size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="methodology-body">

              {/* ── Source Tier Classification ── */}
              <section className="meth-section">
                <h3>Source Tier Classification</h3>
                <p>Every publisher is assigned a tier based on institutional rigor, methodology transparency, data provenance, and market influence. Tier affects weighted scoring in cross-validation.</p>

                <div className="meth-tier-card" style={{ borderLeftColor: '#1E3A8A' }}>
                  <h4>Tier 1 — Investment Banks & Big4 Consulting <span className="meth-weight">Weight: 1.0</span></h4>
                  <p>Primary research with proprietary data, client relationships, and regulatory oversight. Published by firms with direct market participation (deal advisory, underwriting) or consulting engagements with industry participants.</p>
                  <div className="meth-examples">
                    <strong>IB Research:</strong> Goldman Sachs, J.P. Morgan, Morgan Stanley, Bank of America, UBS, Evercore, Lazard, Santander CIB
                  </div>
                  <div className="meth-examples">
                    <strong>Consulting:</strong> Bain, BCG, McKinsey, Deloitte, PwC, Accenture
                  </div>
                  <div className="meth-examples">
                    <strong>Tech Platform Research:</strong> Google (incl. Gamesight, Admix acquisitions), Coatue
                  </div>
                  <div className="meth-rationale">
                    <strong>Why Tier 1:</strong> These firms have deal flow visibility (they advise on the transactions they report), proprietary survey access (10,000+ executive panels), and reputational accountability. Their data is used in board-level decisions at portfolio companies.
                  </div>
                </div>

                <div className="meth-tier-card" style={{ borderLeftColor: '#3b82f6' }}>
                  <h4>Tier 2 — Specialized Data & Research Platforms <span className="meth-weight">Weight: 0.9</span></h4>
                  <p>Domain-specific data providers with systematic collection methodologies. Deep coverage in their vertical but narrower scope than Tier 1.</p>
                  <div className="meth-examples">
                    <strong>Market Intelligence:</strong> PitchBook, Newzoo, Sensor Tower, AppMagic, VGI (Steam data), Niko Partners (Asia gaming)
                  </div>
                  <div className="meth-examples">
                    <strong>M&A Advisory:</strong> Drake Star, DDM, AGC Partners, InvestGame
                  </div>
                  <div className="meth-examples">
                    <strong>Platform Data:</strong> Unity (game engine telemetry), ALDORA, Activate, Ampere Analysis
                  </div>
                  <div className="meth-examples">
                    <strong>Other:</strong> Coinbase (crypto/web3), Deel (employment), Carta (cap tables), Stanford HAI (AI)
                  </div>
                  <div className="meth-rationale">
                    <strong>Why Tier 2:</strong> These firms specialize in data collection — SDK instrumentation (Sensor Tower, AppMagic), deal databases (PitchBook, DDM), or engine telemetry (Unity). Their data is granular and timely but often covers a single market segment. Cross-validation across Tier 2 sources is particularly valuable because their methodologies differ.
                  </div>
                </div>

                <div className="meth-tier-card" style={{ borderLeftColor: '#94a3b8' }}>
                  <h4>Tier 3 — Independent Analysts & Media <span className="meth-weight">Weight: 0.8</span></h4>
                  <p>Individual experts, newsletters, and journalism. Often highly insightful but harder to verify systematically.</p>
                  <div className="meth-examples">
                    <strong>Industry Experts:</strong> Owen Mahoney (ex-Nexon CEO), Mishka Katkoff (Deconstructor of Fun), Brandon Huffman (gaming law)
                  </div>
                  <div className="meth-examples">
                    <strong>Media:</strong> Economist, Financial Times, Washington Post
                  </div>
                  <div className="meth-rationale">
                    <strong>Why Tier 3:</strong> These sources provide qualitative insight, trend interpretation, and contrarian viewpoints that institutional sources miss. Their value is in pattern recognition and narrative, not raw data. When a Tier 3 source aligns with Tier 1-2 data, it strengthens conviction. When they diverge, it signals potential blind spots.
                  </div>
                </div>
              </section>

              {/* ── PMEST Classification ── */}
              <section className="meth-section">
                <h3>PMEST Classification Framework</h3>
                <p>Every report is classified using Ranganathan's five-facet framework. Each facet is an independent filter axis — they compose, not nest.</p>

                <div className="meth-grid">
                  <div className="meth-facet">
                    <h4>Personality (Who)</h4>
                    <p>The publisher, their tier, and the topics they cover. Who wrote this, what authority do they have, and what subject does it address?</p>
                    <div className="meth-fields">Source, Source Tier, Topics, Entities, Keywords</div>
                  </div>
                  <div className="meth-facet">
                    <h4>Matter (What)</h4>
                    <p>The business substance — aligned with Transcend's investment taxonomy. What segment, vertical, platform, genre, and technology does this report cover?</p>
                    <div className="meth-fields">Segment, Vertical, Platform, Genre, Tech Core, Audience, Business Model</div>
                  </div>
                  <div className="meth-facet">
                    <h4>Energy (How)</h4>
                    <p>What the report does — its analytical intent. Is it sizing a market, analyzing deals, forecasting, or providing reference data?</p>
                    <div className="meth-fields">Report Type (Market Sizing, Deal Activity, State of Industry, Deep Dive, etc.), Document Format</div>
                  </div>
                  <div className="meth-facet">
                    <h4>Space (Where)</h4>
                    <p>Geographic focus. Global reports cast wide; regional reports go deep. APAC, MENA, Latin America, India, China each have distinct dynamics.</p>
                    <div className="meth-fields">Geographic Focus (multi-value: Global, APAC, Europe, North America, etc.)</div>
                  </div>
                  <div className="meth-facet">
                    <h4>Time (When)</h4>
                    <p>Publication date and reporting period. Determines freshness. Market sizing data decays in 12 months; deal data in 3 months.</p>
                    <div className="meth-fields">Document Year, Time Period (Q1-Q4, H1/H2, Annual)</div>
                  </div>
                </div>
              </section>

              {/* ── Search Relevance ── */}
              <section className="meth-section">
                <h3>Search Relevance Scoring</h3>
                <p>Search uses <strong>hybrid retrieval</strong> — combining semantic understanding with keyword matching for the best of both worlds.</p>

                <div className="meth-formula">
                  <div className="meth-formula-title">Hybrid Score</div>
                  <code>score = alpha * dense_score + (1 - alpha) * sparse_score</code>
                  <p>Dense = llama-text-embed-v2 (1024-dim semantic vectors). Sparse = BM25 (keyword frequency). Alpha = 0.5 (balanced).</p>
                </div>

                <div className="meth-formula">
                  <div className="meth-formula-title">Relevance Threshold</div>
                  <code>threshold = max(mean - 0.5 * stdev, 0.15)</code>
                  <p>Results below the threshold are dimmed. The threshold adapts to each query — a query with all high-scoring results has a higher bar than one with dispersed scores. Floor at 15% prevents over-filtering.</p>
                </div>

                <div className="meth-formula">
                  <div className="meth-formula-title">Contextual Retrieval</div>
                  <p>Each chunk has a 50-100 token prefix baked into the text before embedding, following Anthropic's contextual retrieval pattern. The prefix summarizes the full document context, so a chunk about ".$196B" is embedded alongside "this is from Bain's 2024 global gaming report, covering software-only revenue." This dramatically reduces false matches from ambiguous numbers.</p>
                </div>
              </section>

              {/* ── Cross-Validation ── */}
              <section className="meth-section">
                <h3>Cross-Validation Methodology</h3>
                <p>When multiple independent sources report the same metric, we can compute consensus values with confidence intervals. This is the quantitative backbone of source intelligence.</p>

                <div className="meth-formula">
                  <div className="meth-formula-title">Claim Extraction</div>
                  <p>Each report's chunks are processed by Gemini 3.5 Flash (with context caching) to extract structured claims: metric name, value, unit, period, scope, source. A deterministic hash of (file_id, category, metric_name, value, period, source) deduplicates claims.</p>
                </div>

                <div className="meth-formula">
                  <div className="meth-formula-title">Weighted Consensus</div>
                  <code>weighted_value = sum(value_i * tier_weight_i) / sum(tier_weight_i)</code>
                  <p>Tier weights: T1 = 1.0, T2 = 0.9, T3 = 0.8, T4 = 0.7. Claims from higher-tier sources pull the consensus toward their values.</p>
                </div>

                <div className="meth-formula">
                  <div className="meth-formula-title">Quality Score (0-100, legacy-compat)</div>
                  <code>Q = min(98, status_base + 2 * min(6, N Sources))</code>
                  <p>Quality Score is a <strong>legacy-compat display value derived from Status</strong>, not an independently weighted score — Status (below) is the canonical trust signal. <code>status_base</code> is a fixed value per Status: Verified 85 · Verified with Variance 68 · Conflicting 55 · Insufficient 40 · Single Source 30. Two metrics sharing the same Status can still show different Quality Scores purely from the source-count bonus.</p>
                </div>

                <div className="meth-formula">
                  <div className="meth-formula-title">Validation Status</div>
                  <p>Status — not Quality Score — is the canonical trust signal. Agreement is measured as relative MAD (median absolute deviation) around the tier-weighted median.</p>
                  <ul className="meth-criteria">
                    <li><strong>Verified</strong> (relative MAD ≤ 5%): Enough sources agree, tightly clustered. High confidence in the value.</li>
                    <li><strong>Verified with Variance</strong> (relative MAD {'>'}5% and ≤ 20%): Enough sources agree, broadly consistent — check scope definitions.</li>
                    <li><strong>Conflicting</strong> (relative MAD {'>'}20%): Enough sources, but they disagree. Usually a scope mismatch (e.g., "gaming market" = software-only vs total interactive entertainment).</li>
                    <li><strong>Insufficient:</strong> Fewer sources than the minimum corroborate — an honest absence, not a number papering over the gap.</li>
                    <li><strong>Single Source:</strong> Exactly one source reports this metric — no consensus possible.</li>
                  </ul>
                </div>
              </section>

              {/* ── Source Intelligence ── */}
              <section className="meth-section">
                <h3>Source Intelligence Signals</h3>
                <p>Meta-analysis of publisher behavior reveals market signals before the data itself does.</p>

                <div className="meth-grid">
                  <div className="meth-facet">
                    <h4>Topic Momentum</h4>
                    <p>When multiple Tier 1-2 sources increase coverage of a topic, it signals institutional conviction forming. AI coverage surging 3x in 2024-2025 preceded the wave of AI gaming acquisitions.</p>
                  </div>
                  <div className="meth-facet">
                    <h4>Entity Velocity</h4>
                    <p>Companies mentioned by 3+ independent sources in a year are strategically significant. Rising mention count = something is happening (fundraise, acquisition, pivot).</p>
                  </div>
                  <div className="meth-facet">
                    <h4>Geographic Shifts</h4>
                    <p>When sources increase regional coverage, investment follows 6-12 months later. APAC/India coverage rising = capital flow direction indicator.</p>
                  </div>
                  <div className="meth-facet">
                    <h4>Tier Composition</h4>
                    <p>When Tier 1 (IB/Big4) share of coverage rises for a sector, it signals institutional deal flow is building. Tier 1 firms write research to support their advisory mandates.</p>
                  </div>
                </div>
              </section>

              {/* ── Data Pipeline ── */}
              <section className="meth-section">
                <h3>Ingestion Pipeline</h3>
                <p>7-step process from PDF to queryable intelligence:</p>
                <ol className="meth-pipeline">
                  <li><strong>OCR Extraction</strong> — Mistral OCR 3 converts PDFs to structured markdown (tables preserved, images referenced)</li>
                  <li><strong>Visual Detection</strong> — Regex identifies chunks containing charts/graphs for metadata tagging</li>
                  <li><strong>Semantic Chunking</strong> — Section-aware splitting (1,400 char target, 150 overlap, markdown header recognition)</li>
                  <li><strong>Contextual Prefix</strong> — Gemini 3.1 Flash Lite generates 50-100 token situating prefix per chunk (context-cached per report for ~80% token savings)</li>
                  <li><strong>PMEST Classification</strong> — Same model classifies the full report across all 34 PMEST dimensions</li>
                  <li><strong>Hybrid Embedding</strong> — Dense (llama-text-embed-v2, 1024-dim) + Sparse (BM25) vectors</li>
                  <li><strong>Multi-System Write</strong> — Pinecone upsert + GDrive properties + Sheet tracker + Classification shortcuts</li>
                </ol>
              </section>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeBase;
